import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import debounce from "debounce-promise";
import { produce } from "immer";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";

import BaseErrors from "@components/fields/BaseErrors";
import BooleanRadioButtonGroup from "@components/fields/BooleanRadioButtonGroup";
import CareProfileInput from "@components/fields/CareProfileInput";
import Select from "@components/fields/Select";
import styles from "@components/forms/careProfile/CareProfile.module.css";
import CareProfileFormCondition from "@components/forms/careProfile/CareProfileFormCondition";
import Form from "@components/forms/Form";
import {
  CareProfileCaregiverTypeEnum,
  CareProfileCognitiveStatusEnum,
  CareProfileCurrentCareTransitionEnum,
  CareProfileFallCountEnum,
  CareProfileFieldNameEnum,
  CareProfileFieldScopeEnum,
  CareProfileResidenceTypeEnum,
  CareProfileStabilitySupportEnum,
  CareProfileStageLimitedEnum,
  CareProfileUpdateDocument,
  CareProfileUpdateMutation,
  CareProfileUpdateMutationVariables,
  DurableMedicalEquipmentEnum,
  GetCareProfileFieldRulesQuery,
  GetJourneyWithCareProfileQuery,
  JourneyUpdateDocument,
  JourneyUpdateMutation,
  JourneyUpdateMutationVariables,
  StageEnum,
  YesNoDontKnowEnum
} from "@typing/Generated";
import { handleMutationResponse, ThingWithErrors } from "@utils/formUtils";
import {
  optionsForBoolean,
  optionsForCaregiverType,
  optionsForCognitiveStatus,
  optionsForCurrentCareTransition,
  optionsForDurableMedicalEquipment,
  optionsForFallCount,
  optionsForResidenceType,
  optionsForStabilitySupport,
  optionsForStage,
  optionsForStageLimited,
  optionsForYesNoDontKnow
} from "@utils/optionUtils";

type CareProfileFieldRules = GetCareProfileFieldRulesQuery["careProfileFieldRules"];

type Props = {
  careProfileFieldRules: CareProfileFieldRules;
  journey: GetJourneyWithCareProfileQuery["journey"];
};

type FormData = {
  caregiverTypes?: CareProfileCaregiverTypeEnum[];
  caregiverTypesOther?: string | null;
  cognitiveStatuses?: CareProfileCognitiveStatusEnum[];
  currentCareTransitions?: CareProfileCurrentCareTransitionEnum[];
  currentCareTransitionsOther?: string | null;
  dayToDaySupportNeeded?: YesNoDontKnowEnum | null;
  durableMedicalEquipment?: DurableMedicalEquipmentEnum[];
  fallCount?: CareProfileFallCountEnum | null;
  legallyDisabled?: boolean | null;
  residenceType?: CareProfileResidenceTypeEnum | null;
  residenceTypeOther?: string | null;
  stabilitySupports?: CareProfileStabilitySupportEnum[] | null;
  stage?: StageEnum | null;
  stageBathingAndDressing?: CareProfileStageLimitedEnum | null;
  stageContinence?: CareProfileStageLimitedEnum | null;
  stageEating?: CareProfileStageLimitedEnum | null;
  stageHousekeeping?: CareProfileStageLimitedEnum | null;
  stageMealPrep?: CareProfileStageLimitedEnum | null;
  stageShopping?: CareProfileStageLimitedEnum | null;
  stageToileting?: CareProfileStageLimitedEnum | null;
  stageTransportation?: CareProfileStageLimitedEnum | null;
};

const CareProfileCareAndHomeForm = ({ careProfileFieldRules, journey }: Props) => {
  const intl = useIntl();

  const careProfile = journey.careProfile;

  const defaultValues: FormData = {
    caregiverTypes: careProfile?.caregiverTypes?.caregiverTypesOptions ?? [],
    caregiverTypesOther: careProfile.caregiverTypes?.other ?? null,
    cognitiveStatuses: careProfile.cognitiveStatuses ?? [],
    currentCareTransitions: careProfile.currentCareTransitions?.currentCareTransitionsOptions ?? [],
    currentCareTransitionsOther: careProfile.currentCareTransitions?.other ?? null,
    dayToDaySupportNeeded: careProfile.dayToDaySupportNeeded ?? null,
    durableMedicalEquipment: careProfile.durableMedicalEquipment ?? null,
    fallCount: careProfile.fallCount ?? null,
    legallyDisabled: careProfile.legallyDisabled ?? null,
    residenceType: careProfile.residenceType?.residenceTypeOption ?? null,
    residenceTypeOther: careProfile.residenceType?.other ?? null,
    stabilitySupports: careProfile.stabilitySupports ?? [],
    stage: journey.stage ?? null,
    stageBathingAndDressing: careProfile.stageBathingAndDressing ?? null,
    stageContinence: careProfile.stageContinence ?? null,
    stageEating: careProfile.stageEating ?? null,
    stageHousekeeping: careProfile.stageHousekeeping ?? null,
    stageMealPrep: careProfile.stageMealPrep ?? null,
    stageShopping: careProfile.stageShopping ?? null,
    stageToileting: careProfile.stageToileting ?? null,
    stageTransportation: careProfile.stageTransportation ?? null
  };

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateCareProfile] = useMutation<CareProfileUpdateMutation, CareProfileUpdateMutationVariables>(
    CareProfileUpdateDocument
  );

  const [updateJourney] = useMutation<JourneyUpdateMutation, JourneyUpdateMutationVariables>(JourneyUpdateDocument);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      caregiverTypes: yup.array().of(yup.string().oneOf(Object.values(CareProfileCaregiverTypeEnum)).defined()),
      caregiverTypesOther: yup.string().nullable(),
      cognitiveStatuses: yup.array().of(yup.string().oneOf(Object.values(CareProfileCognitiveStatusEnum)).defined()),
      currentCareTransitions: yup
        .array()
        .of(yup.string().oneOf(Object.values(CareProfileCurrentCareTransitionEnum)).defined()),
      currentCareTransitionsOther: yup.string().nullable(),
      dayToDaySupportNeeded: yup.string().oneOf(Object.values(YesNoDontKnowEnum)).nullable(),
      durableMedicalEquipment: yup.array().of(yup.string().oneOf(Object.values(DurableMedicalEquipmentEnum)).defined()),
      fallCount: yup.string().oneOf(Object.values(CareProfileFallCountEnum)).nullable(),
      legallyDisabled: yup.boolean().nullable(),
      residenceType: yup.string().oneOf(Object.values(CareProfileResidenceTypeEnum)).nullable(),
      residenceTypeOther: yup.string().nullable(),
      stabilitySupports: yup.array().of(yup.string().oneOf(Object.values(CareProfileStabilitySupportEnum)).defined()),
      stage: yup.string().oneOf(Object.values(StageEnum)).nullable(),
      stageBathingAndDressing: yup.string().oneOf(Object.values(CareProfileStageLimitedEnum)).nullable(),
      stageContinence: yup.string().oneOf(Object.values(CareProfileStageLimitedEnum)).nullable(),
      stageEating: yup.string().oneOf(Object.values(CareProfileStageLimitedEnum)).nullable(),
      stageHousekeeping: yup.string().oneOf(Object.values(CareProfileStageLimitedEnum)).nullable(),
      stageMealPrep: yup.string().oneOf(Object.values(CareProfileStageLimitedEnum)).nullable(),
      stageShopping: yup.string().oneOf(Object.values(CareProfileStageLimitedEnum)).nullable(),
      stageToileting: yup.string().oneOf(Object.values(CareProfileStageLimitedEnum)).nullable(),
      stageTransportation: yup.string().oneOf(Object.values(CareProfileStageLimitedEnum)).nullable()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const submitCareProfile = useCallback(async () => {
    const valuesWithoutErrors = produce(form.getValues(), draftValues => {
      Object.keys(form.formState.errors).forEach(field => {
        delete draftValues[field as keyof FormData];
      });
    });

    setBaseErrors([]);

    const caregiverTypes = valuesWithoutErrors.caregiverTypes;
    const currentCareTransitions = valuesWithoutErrors.currentCareTransitions;
    const residenceType = valuesWithoutErrors.residenceType;

    return updateCareProfile({
      variables: {
        ...valuesWithoutErrors,
        caregiverTypes: caregiverTypes && {
          caregiverTypesOptions: caregiverTypes,
          other: caregiverTypes.includes(CareProfileCaregiverTypeEnum.OTHER)
            ? valuesWithoutErrors.caregiverTypesOther
            : null
        },
        currentCareTransitions: currentCareTransitions && {
          currentCareTransitionOptions: currentCareTransitions,
          other: currentCareTransitions.includes(CareProfileCurrentCareTransitionEnum.OTHER)
            ? valuesWithoutErrors.currentCareTransitionsOther
            : null
        },
        id: journey.careProfile.id,
        residenceType: residenceType && {
          other: residenceType === CareProfileResidenceTypeEnum.OTHER ? valuesWithoutErrors.residenceTypeOther : null,
          residenceTypeOption: residenceType
        }
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.careProfileUpdate,
        intl,
        onError: () => {},
        onSuccess: () => {},
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.careProfile"
      });
    });
  }, [form, intl, journey.careProfile.id, updateCareProfile]);

  const submitJourney = useCallback(async () => {
    const valuesWithoutErrors = produce(form.getValues(), draftValues => {
      Object.keys(form.formState.errors).forEach(field => {
        delete draftValues[field as keyof FormData];
      });
    });

    setBaseErrors([]);
    return updateJourney({
      variables: {
        ...valuesWithoutErrors,
        id: journey.id
      }
    }).then(response => {
      const data = [response.data?.journeyUpdate] as ThingWithErrors[];
      handleMutationResponse<FormData>({
        data,
        intl,
        onError: () => {},
        onSuccess: () => {},
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.journey"
      });
    });
  }, [form, intl, journey.id, updateJourney]);

  useEffect(() => {
    const debouncedSubmitCareProfile = debounce(submitCareProfile, 2000);
    const debouncedSubmitJourney = debounce(submitJourney, 2000);
    const subscription = form.watch((_, { name }) => {
      if (name === "stage") {
        form.handleSubmit(debouncedSubmitJourney, debouncedSubmitJourney)();
      } else {
        form.handleSubmit(debouncedSubmitCareProfile, debouncedSubmitCareProfile)();
      }
    });
    return () => subscription.unsubscribe();
  }, [form, submitCareProfile, submitJourney]);

  const booleanOptions = optionsForBoolean(intl);
  const caregiverTypeOptions = optionsForCaregiverType(intl);
  const cognitiveStatusOptions = optionsForCognitiveStatus(intl);
  const currentCareTransitionOptions = optionsForCurrentCareTransition(intl);
  const durableMedicalEquipmentOptions = optionsForDurableMedicalEquipment(intl);
  const fallCountOptions = optionsForFallCount(intl);
  const residenceTypeOptions = optionsForResidenceType(intl);
  const stabilitySupportOptions = optionsForStabilitySupport(intl);
  const stageOptions = optionsForStage(intl);
  const stageLimitedOptions = optionsForStageLimited(intl);
  const yesNoDontKnowOptions = optionsForYesNoDontKnow(intl);

  return (
    <Form hookForm={form}>
      <BaseErrors errors={baseErrors} />
      <div className={styles.formSectionRow}>
        <div className={styles.formControlsColumn}>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.STAGE}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="stage"
            scope={CareProfileFieldScopeEnum.JOURNEY}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.stage}
              label={intl.formatMessage({ id: "forms.careProfileNew.stageLabel" })}
              name="stage"
              options={stageOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.STAGE_BATHING_AND_DRESSING}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="stageBathingAndDressing"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.stageBathingAndDressing}
              label={intl.formatMessage({ id: "forms.careProfileNew.stageBathingAndDressingLabel" })}
              name="stageBathingAndDressing"
              options={stageLimitedOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.STAGE_CONTINENCE}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="stageContinence"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.stageContinence}
              label={intl.formatMessage({ id: "forms.careProfileNew.stageContinenceLabel" })}
              name="stageContinence"
              options={stageLimitedOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.STAGE_EATING}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="stageEating"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.stageEating}
              label={intl.formatMessage({ id: "forms.careProfileNew.stageEatingLabel" })}
              name="stageEating"
              options={stageLimitedOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.STAGE_HOUSEKEEPING}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="stageHousekeeping"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.stageHousekeeping}
              label={intl.formatMessage({ id: "forms.careProfileNew.stageHousekeepingLabel" })}
              name="stageHousekeeping"
              options={stageLimitedOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.STAGE_MEAL_PREP}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="stageMealPrep"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.stageMealPrep}
              label={intl.formatMessage({ id: "forms.careProfileNew.stageMealPrepLabel" })}
              name="stageMealPrep"
              options={stageLimitedOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.STAGE_SHOPPING}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="stageShopping"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.stageShopping}
              label={intl.formatMessage({ id: "forms.careProfileNew.stageShoppingLabel" })}
              name="stageShopping"
              options={stageLimitedOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.STAGE_TOILETING}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="stageToileting"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.stageToileting}
              label={intl.formatMessage({ id: "forms.careProfileNew.stageToiletingLabel" })}
              name="stageToileting"
              options={stageLimitedOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.STAGE_TRANSPORTATION}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="stageTransportation"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.stageTransportation}
              label={intl.formatMessage({ id: "forms.careProfileNew.stageTransportationLabel" })}
              name="stageTransportation"
              options={stageLimitedOptions}
            />
          </CareProfileFormCondition>
        </div>
        <div className={styles.formControlsColumn}>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.STABILITY_SUPPORTS}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="stabilitySupports"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.stabilitySupports}
              isMulti
              label={intl.formatMessage({ id: "forms.careProfileNew.stabilitySupportsLabel" })}
              mutuallyExclusiveValues={[CareProfileStabilitySupportEnum.NONE]}
              name="stabilitySupports"
              options={stabilitySupportOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.CURRENT_CARE_TRANSITIONS}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="currentCareTransitions"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.currentCareTransitions}
              isMulti
              label={intl.formatMessage({ id: "forms.careProfileNew.currentCareTransitionsLabel" })}
              mutuallyExclusiveValues={[CareProfileCurrentCareTransitionEnum.NONE]}
              name="currentCareTransitions"
              options={currentCareTransitionOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.CURRENT_CARE_TRANSITIONS_OTHER}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="currentCareTransitionsOther"
            otherField="currentCareTransitions"
            otherValue={CareProfileCurrentCareTransitionEnum.OTHER}
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.currentCareTransitionsOther}
              label={intl.formatMessage({ id: "forms.careProfileNew.currentCareTransitionsOtherLabel" })}
              register={form.register("currentCareTransitionsOther")}
              spellcheck
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.COGNITIVE_STATUSES}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="cognitiveStatuses"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.cognitiveStatuses}
              isMulti
              label={intl.formatMessage({ id: "forms.careProfileNew.cognitiveStatusesLabel" })}
              mutuallyExclusiveValues={[CareProfileCognitiveStatusEnum.NONE]}
              name="cognitiveStatuses"
              options={cognitiveStatusOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.LEGALLY_DISABLED}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="legallyDisabled"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <BooleanRadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.legallyDisabled}
              inTracker={false}
              label={intl.formatMessage({ id: "forms.careProfileNew.legallyDisabledLabel" })}
              name="legallyDisabled"
              options={booleanOptions}
            />
          </CareProfileFormCondition>
        </div>
        <div className={styles.formControlsColumn}>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.FALL_COUNT}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="fallCount"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.fallCount}
              label={intl.formatMessage({ id: "forms.careProfileNew.fallCountLabel" })}
              name="fallCount"
              options={fallCountOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.RESIDENCE_TYPE}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="residenceType"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.residenceType}
              label={intl.formatMessage({ id: "forms.careProfileNew.residenceTypeLabel" })}
              name="residenceType"
              options={residenceTypeOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.RESIDENCE_TYPE_OTHER}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="residenceTypeOther"
            otherField="residenceType"
            otherValue={CareProfileResidenceTypeEnum.OTHER}
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.residenceTypeOther}
              label={intl.formatMessage({ id: "forms.careProfileNew.residenceTypeOtherLabel" })}
              register={form.register("residenceTypeOther")}
              spellcheck
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.CAREGIVER_TYPES}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="caregiverTypes"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.caregiverTypes}
              isMulti
              label={intl.formatMessage({ id: "forms.careProfileNew.caregiverTypesLabel" })}
              name="caregiverTypes"
              options={caregiverTypeOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.CAREGIVER_TYPES_OTHER}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="caregiverTypesOther"
            otherField="caregiverTypes"
            otherValue={CareProfileCaregiverTypeEnum.OTHER}
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.caregiverTypesOther}
              label={intl.formatMessage({ id: "forms.careProfileNew.caregiverTypesOtherLabel" })}
              register={form.register("caregiverTypesOther")}
              spellcheck
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.DAY_TO_DAY_SUPPORT_NEEDED}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="dayToDaySupportNeeded"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.dayToDaySupportNeeded}
              label={intl.formatMessage({ id: "forms.careProfileNew.dayToDaySupportNeededLabel" })}
              name="dayToDaySupportNeeded"
              options={yesNoDontKnowOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.DURABLE_MEDICAL_EQUIPMENT}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="durableMedicalEquipment"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.durableMedicalEquipment}
              isMulti
              label={intl.formatMessage({ id: "forms.careProfileNew.durableMedicalEquipmentLabel" })}
              name="durableMedicalEquipment"
              options={durableMedicalEquipmentOptions}
            />
          </CareProfileFormCondition>
        </div>
      </div>
    </Form>
  );
};

export default CareProfileCareAndHomeForm;
